// components/ImpersonationBanner.jsx
import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { getInitialize } from '../../actions/action_auth';

export default function ImpersonationBanner() {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user?.user);
  const bannerRef = useRef(null);
  const isImpersonating = !!user?.isImpersonating;

  // Push the rest of the app down by the banner's real height,
  // and flag <body> so fixed-position layout pieces (sidebar etc.) can react.
  useEffect(() => {
    const root = document.documentElement;

    if (isImpersonating && bannerRef.current) {
      const height = bannerRef.current.offsetHeight;
      root.style.setProperty('--impersonation-banner-height', `${height}px`);
      document.body.classList.add('is-impersonating');
    } else {
      root.style.setProperty('--impersonation-banner-height', '0px');
      document.body.classList.remove('is-impersonating');
    }

    return () => {
      root.style.setProperty('--impersonation-banner-height', '0px');
      document.body.classList.remove('is-impersonating');
    };
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  const onExitImpersonation = async () => {
    try {
      const impersonationToken = localStorage.getItem('accessToken');

      const res = await axios.post(
        '/api/auth/impersonate/stop',
        {},
        { headers: { Authorization: `Bearer ${impersonationToken}` } }
      );

      const { accessToken } = res.data;

      localStorage.setItem('accessToken', accessToken);
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      localStorage.removeItem('adminAccessToken');
      localStorage.removeItem('impersonationSessionId');
      localStorage.removeItem('impersonatedUser');

      await dispatch(getInitialize());

      window.location.href = '/jobs';
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
      alert(err?.response?.data?.error || 'Failed to stop impersonation');
    }
  };

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;

  return (
    <div
      ref={bannerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999999, // above everything, including a fixed sidebar
        width: '100%',
        background: 'linear-gradient(180deg, #FFC94A 0%, #FFB800 100%)',
        borderBottom: '1px solid #E6A700',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '14px',
        flexWrap: 'wrap'
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#3A2B00',
          fontSize: '14px',
          fontWeight: 500,
          letterSpacing: '0.1px'
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <path d="M12 2L1 21h22L12 2z" stroke="#3A2B00" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M12 9v5" stroke="#3A2B00" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="17" r="1" fill="#3A2B00" />
        </svg>
        You're impersonating <strong style={{ fontWeight: 700 }}>{displayName} ({user.role})</strong>
        {user.impersonatedBy?.name ? (
          <span style={{ opacity: 0.75, fontWeight: 400 }}>
            &nbsp;— signed in as {user.impersonatedBy.name}
          </span>
        ) : null}
      </span>

      <button
        onClick={onExitImpersonation}
        onMouseEnter={e => (e.currentTarget.style.background = '#C41E1E')}
        onMouseLeave={e => (e.currentTarget.style.background = '#DC2626')}
        style={{
          background: '#DC2626',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '6px',
          padding: '6px 14px',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'background 0.15s ease',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)'
        }}
      >
        Stop impersonating
      </button>
    </div>
  );
}